import React from "react";
import { InfoTooltip } from "../info-tooltip";
import TooltipContent from "../tooltip-content";
import { useStoreActions, useStoreState } from "app/state/store/hooks";

type Placement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "bottom-end"
  | "bottom-start"
  | "top-end"
  | "top-start"
  | undefined;

interface PlatformWalkthroughTooltipProps {
  step: number;
  children?: React.ReactNode;
  placement?: Placement;
}

const explorerTooltipContents = [
  {
    title: "AIDA Explorer navigates you to IATI data. (1/6) ",
    content:
      "You can discover IATI data through charts, visualisations and tables.",
  },
  {
    title:
      "AIDA Publisher helps you and your organisations to publish IATI data.  (2/6) ",
    content: "This section directs you to publisher pages and tools.",
  },
  {
    title: "You can filter the data however you want. (3/6) ",
    content:
      "The filter is activated by default to show the recent year's data. This is to focus on the recent activity on IATI. This can also be modified form this tab.",
  },
  {
    title: "Transaction type is selected besides the filters.  (4/6) ",
    content:
      "Each activity can have one or multiple transaction types. Therefore, it is good to explore different transaction types to see different dimensions of the activity data.",
  },
  {
    title:
      "You can download or share all the data reflected on the page.  (5/6) ",
    content: "",
  },
  {
    title:
      "You can expand the chart by clicking on expand arrow icons on the charts. (6/6) ",
    content:
      "This helps you have a wider view of the chart with more detailed information. Some charts which do not have the expand arrow icon cannot be expanded.",
  },
];

const publisherTooltipContents = [
  {
    title: "AIDA Explorer navigates you to IATI data. (1/8)",
    content:
      "You can discover IATI data through charts, visualisations and tables.",
  },
  {
    title:
      "AIDA Publisher helps you and your organisations to publish IATI data. (2/8)",
    content: "This section directs you to publisher pages and tools.",
  },
  {
    title: "This button kickstarts the publishing process. (3/8)",
    content:
      "Publishing with AIDA is a simple and fast process that happens in this very window. You can start this process quickly from here.",
  },
  {
    title: "This is where you will navigate the publishing process. (4/8)",
    content:
      "As you progress, the state of this window will change and help you through publishing.",
  },
  {
    title: "You can see a summary of your favourites here. (5/8)",
    content: "",
  },
  {
    title: "Use this arrow to be directed to “Your Favourites” page. (6/8)",
    content:
      "This page has all of your favourites files in table format for easy navigation and overview.",
  },
  {
    title: "This is where you see snapshots of your published IATI data. (7/8)",
    content:
      "It is normal to see this empty till you connect your organisation data or start publishing through your account.",
  },
  {
    title: "Use this arrow to be directed to “Your Data Hub” page. (8/8)",
    content:
      "Your Data Hub might be the most important page for a publisher on AIDA. It's your data management centre in which you can publish, draft and visualise your data.",
  },
];

const PlatformWalkthroughTooltip: React.FC<PlatformWalkthroughTooltipProps> = ({
  step,
  children,
  placement,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const variant = window.location.pathname.includes("/publisher")
    ? "publisher"
    : "explorer";
  const explorerStep = useStoreState(
    (state) => state.platformWalkthrough.explorerStep
  );

  const setExplorerStep = useStoreActions(
    (actions) => actions.platformWalkthrough.setExplorerStep
  );

  const publisherStep = useStoreState(
    (state) => state.platformWalkthrough.publisherStep
  );

  const setPublisherStep = useStoreActions(
    (actions) => actions.platformWalkthrough.setPublisherStep
  );

  const { setStep, localStep, contents } = React.useMemo(() => {
    if (variant === "explorer") {
      return {
        localStep: explorerStep,
        setStep: setExplorerStep,
        contents: explorerTooltipContents,
      };
    } else {
      return {
        localStep: publisherStep,
        setStep: setPublisherStep,
        contents: publisherTooltipContents,
      };
    }
  }, [variant, explorerStep, setExplorerStep, publisherStep, setPublisherStep]);

  React.useEffect(() => {
    if (ref.current && localStep === step) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [localStep]);

  return (
    <InfoTooltip
      mode={variant === "explorer" ? "blue" : "green"}
      open={localStep === step}
      info={({ tooltipColors }) => (
        <TooltipContent
          title={contents[step - 1]?.title ?? ""}
          content={contents[step - 1]?.content ?? ""}
          color={tooltipColors.text}
          onClose={() => {
            setStep(-1);
          }}
          onBack={() => {
            setStep(step - 1);
          }}
          onNext={() => {
            if (step === contents.length) {
              setStep(-1);
            } else {
              setStep(step + 1);
            }
          }}
          nextText={step === contents.length ? "Exit Walkthrough" : "Next"}
        />
      )}
      placement={placement ?? "bottom"}
      arrow={true}
      width="320px"
    >
      <div ref={ref}>{children}</div>
    </InfoTooltip>
  );
};

export default PlatformWalkthroughTooltip;
